<template>
    <div id="DrugsProtocolSetupManagement-Container">
        <h4 v-if="!this.$attrs.editMode" class="page-title text-truncate">
            {{ $t('DrugSetupProtocol.AddTitle') }}
        </h4>
        <h4 v-else class="page-title text-truncate">{{ $t('DrugSetupProtocol.EditTitle') }}</h4>

        <div class="page-height">
            <div
                class="alert alert-fill-warning d-flex align-items-center m-3"
                role="alert"
                v-if="isReadOnly"
            >
                <i class="mdi mdi-alert-circle me-2" style="font-size: 1.5rem;"></i>
                <div class="flex-grow-1">
                    <span>{{ $t('Shared.ReadOnlyPermission') }}</span>
                </div>
            </div>

            <b-row>
                <b-col lg="4" md="4" sm="12">
                    <MdEdit
                        v-bind:Label="$t('Shared.Description')"
                        v-bind:Name="'Description'"
                        v-bind:TypeProp="'text'"
                        v-bind:MaxLengthProp="50"
                        v-model="editableItem.description"
                        v-bind:isReadOnly="isReadOnly"
                        v-bind:Id="'input_description'"
                        v-bind:isRequired="true"
                        v-bind:isValid="isDescriptionValid"
                    />
                </b-col>
                <b-col lg="4" md="4" sm="12">
                    <MdLookupMultiple
                        v-bind:modelFilter="modelFilter"
                        v-bind:OptionParamList="ParamList"
                        v-bind:Label="$t('Admission.Diagnosis', { num: '1' })"
                        v-bind:ModelCode="editableItem.diagCode"
                        v-bind:ModelDescription="editableItem.diagDescription"
                        v-bind:RefCode="'Diagnosis'"
                        :IsDisabled="isReadOnly"
                        v-bind:Api="'DiagnosisICD10Providers'"
                        v-bind:Fields="BaseFields"
                        v-bind:FieldCode="'code'"
                        v-bind:FieldDescription="'name'"
                        :SearchTitle="$t('Shared.Diagnosis')"
                        :GetValues="onCodeConditionOk"
                        :InvalidFeedback="$t('Shared.InvalidCodeMsg')"
                        :SearchFieldCode="'Code'"
                    />
                </b-col>
                <b-col lg="1" md="1" sm="12">
                    <MdCheckBox
                        v-bind:Label="$t('Shared.Active')"
                        v-bind:Name="'Active'"
                        v-bind:ClassProp="'AlignCheck'"
                        v-bind:CheckedValue="true"
                        v-bind:UncheckedValue="false"
                        v-model="editableItem.isActive"
                    />
                </b-col>
            </b-row>

            <MdTitle
                v-bind:Text="$tc('Shared.Note', 2)"
                v-bind:isSubtitle="true"
                v-bind:Name="'T_Notes'"
            />
            <b-row>
                <b-col md="12" lg="12" sm="12">
                    <MdMemo
                        v-bind:Name="'textarea_notes'"
                        v-bind:Rows="'3'"
                        v-bind:isReadOnly="isReadOnly"
                        v-bind:MaxRows="'6'"
                        v-bind:MaxLength="'500'"
                        v-model="editableItem.remarks"
                    />
                </b-col>
            </b-row>

            <MdTitle
                v-bind:VisibleInfo="false"
                v-bind:Text="$t('Medication.Medications')"
                v-bind:isSubtitle="true"
                v-bind:Name="'T_TitleSearch'"
            />
            <b-row>
                <div class="col-sm-12 col-md-3">
                    <MdButton
                        v-bind:Label="$t('DrugSetupProtocol.AddMedication')"
                        v-bind:ClassProp="
                            isReadOnly
                                ? 'btn btn-primary my-3 btn-block'
                                : 'btn btn-primary my-3 btn-block btn-login'
                        "
                        v-bind:IconClass="'ti-plus pr-1'"
                        v-on:click="addMedication"
                        v-bind:isReadOnly="isReadOnly"
                        v-bind:Name="'btn_add_AddMedication'"
                    >
                    </MdButton>
                </div>

                <ModalTablePaginationServer
                    v-bind:Resource="DrugNameCall"
                    v-bind:TypeOption="'Drugs'"
                    @getCodeConditionOK="onCodeConditionOk($event)"
                    v-bind:Fields="this.BaseFieldsDrug"
                    v-bind:FieldCode="'code'"
                    v-bind:FieldDescription="'drugName'"
                    v-bind:TitleWindow="$t('Medication.Drugs')"
                    v-bind:ModalId="medicationsDlgId"
                ></ModalTablePaginationServer>
            </b-row>

            <b-row v-if="editableItem.prescriptions.length === 0">
                <div class="col-12">
                    <div v-bind:style="emptyRecordsStyle" role="alert" aria-live="polite">
                        <div class="text-center my-2">{{ $t('MdTable.nRecShow') }}</div>
                    </div>
                </div>
            </b-row>

            <b-row>
                <b-col md="12" lg="12" sm="12">
                    <div class="zebraAccordion">
                        <div
                            class="zebraRow"
                            v-for="(item, index) in editableItem.prescriptions"
                            :key="index"
                        >
                            <div class="accordion" role="tablist">
                                <div
                                    class="zebraItem"
                                    :class="{ 'text-error': invalidStates[index] }"
                                    v-b-toggle="'accordion-' + index"
                                >
                                    <div class="container-fluid">
                                        <div
                                            class="row d-flex justify-content-between align-items-center"
                                        >
                                            <div class="col-md-11">
                                                <div class="d-flex align-items-center">
                                                    <span
                                                        @click="removeMedication($event, index)"
                                                        @click.stop
                                                        class="rounded-circle tableActionBTN tableBTNtext tableActionBTN-danger ikon med-bin"
                                                    ></span>
                                                    <span class="mx-2">
                                                        <span
                                                            >{{ item.drugName }} -
                                                            {{ item.activeIngredient }} -
                                                            {{ item.drugCode }} - {{ item.form }}
                                                            {{ getTitrationOrderLabel(item) }}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <b-card no-body>
                                <b-collapse
                                    :id="'accordion-' + index"
                                    accordion="my-accordion"
                                    role="tabpanel"
                                    v-model="visibleStates[index]"
                                >
                                    <DrugProtocolSetup
                                        :key="index"
                                        v-model="editableItem.prescriptions[index]"
                                        :index="index"
                                        :ref="`StandingOrderDrug_${index}`"
                                        v-bind:ActionList="ActionList"
                                        v-bind:RouteNameList="RouteNameList"
                                        v-bind:WhenList="WhenList"
                                        v-bind:ReasonList="ReasonList"
                                        v-bind:PharmacyOption="PharmacyOption"
                                    >
                                    </DrugProtocolSetup>
                                </b-collapse>
                            </b-card>
                        </div>
                    </div>
                </b-col>
            </b-row>
        </div>

        <div class="space-actions"></div>
        <div class="page-actions">
            <MdButton
                v-bind:Label="$t('Shared.Back')"
                v-bind:Name="'btn_protocol_setup_list'"
                v-bind:ClassProp="'secondary mx-2 btn-login'"
                v-on:click="discardChanges"
            />

            <MdButton
                v-bind:Label="$t('Shared.Save')"
                v-bind:ClassProp="isReadOnly ? 'primary mx-2' : 'primary btn-login mx-2'"
                v-on:click="saveOrEdit"
                v-bind:isReadOnly="!saveButtonEnabled"
                v-bind:Name="'btn_save_protocol_setup'"
            />
        </div>
    </div>
</template>

<script>
import GlobalService from '@/services/global.service';
import MedicationService from '@/services/medication.service.js';
import DrugService from '@/services/drug.service';
import ShowMessage from '@/components/messages/ShowMessage.js';
import DBFunctions from '@/common/DBFunctions.js';
import DrugProtocolSetup from './drugProtocolSetup';
import PharmacyOptions from '@/services/pharmacyoption.service';
import { EventBus } from '@/event-bus.js';
import Utils from '@/common/utils';

export default {
    name: 'DrugsProtocolSetupManagement',
    mixins: [ShowMessage, DBFunctions],
    components: {
        DrugProtocolSetup
    },

    data() {
        return {
            isReadOnly: false,
            editableItem: {
                code: Utils.generateRecID()
                    .toUpperCase()
                    .substr(0, 15),
                description: null,
                diagCode: null,
                diagDescription: null,
                remarks: null,
                isActive: true,
                prescriptions: []
            },
            isCodeValid: true,
            isDescriptionValid: true,
            BaseFields: [
                { key: 'name', sortable: true, label: this.$t('Shared.Description') },
                { key: 'code', sortable: true, label: this.$t('Shared.Code') }
            ],
            BaseFieldsDrug: [
                { key: 'drugName', sortable: true, label: this.$t('Shared.DrugName') },
                {
                    key: 'activeIngredients',
                    sortable: true,
                    label: this.$t('Shared.activeIngredients')
                },
                { key: 'dosage', sortable: true, label: this.$t('Shared.Dosage') },
                { key: 'form', sortable: true, label: this.$t('Shared.Form') },
                { key: 'code', sortable: true, label: this.$t('Shared.Code') }
            ],
            emptyRecordsStyle: {
                backgroundColor: '#CF0',
                fontWeight: 'bold !important',
                textTransform: 'uppercase',
                padding: '2px',
                borderRadius: '50px',
                animation: 'fadeOut 10s ease-in-out infinite'
            },
            medicationsDlgId: 'medicationDlg',
            DrugNameCall: 'PatientMedications/AllDrugs?',
            ActionList: [],
            RouteNameList: [],
            WhenList: [],
            ReasonList: [],
            PharmacyOption: {},
            pendingChanges: false,
            OffRoute: false,
            WasSaved: false,
            prescriptionsInvalid: null,
            visibleStates: [],
            invalidStates: [],
            codeExist: false,
            duplicateTitration: false,
            ParamList: [
                {
                    code: 1,
                    description: this.$t('Shared.AllDiagnoses'),
                    resource: 'DiagnosisICD10Providers?'
                },
                {
                    code: 2,
                    description: this.$t('Shared.MostDiagnosesUsed'),
                    resource: 'DiagnosisICD10Providers?mustUsed=true'
                }
            ],
            modelFilter: {
                code: 1,
                description: this.$t('Shared.AllDiagnoses'),
                resource: 'DiagnosisICD10Providers'
            }
        };
    },

    computed: {
        saveButtonEnabled() {
            return !this.isReadOnly && this.pendingChanges;
        }
    },

    methods: {
        getTitrationOrderLabel(item) {
            const drugs = this.editableItem.prescriptions.filter(x => x.drugCode === item.drugCode);
            if (drugs.length > 1) {
                return `- [${item.titrationOrder}]`;
            }

            return '';
        },

        discardChanges() {
            this.$router.push({ name: 'DrugsProtocolSetupList' });
        },

        IsAccordionVisible(value) {
            return value == this.prescriptionsInvalid;
        },

        async saveOrEdit() {
            this.prescriptionsInvalid = null;
            if (this.validate_required()) {
                if (this.validate_prescriptions()) {
                    if (this.$attrs.editMode) {
                        await this.update();
                    } else {
                        await this.insert();
                    }
                } else if (this.editableItem.prescriptions.length === 0) {
                    this.OffRoute = false;
                    this.showMessage(
                        this.$tc('Msg.Error'),
                        this.$t('DrugSetupProtocol.EmptyDrugMessageError'),
                        'errorAutoHide'
                    );
                } else if (this.duplicateTitration) {
                    this.OffRoute = false;
                    this.showMessage(
                        this.$tc('Msg.Error'),
                        this.$t('DrugSetupProtocol.DuplicateTitration'),
                        'errorAutoHide'
                    );
                } else {
                    this.OffRoute = false;
                    this.showMessage(
                        this.$tc('Msg.Error'),
                        this.$t('Msg.CheckRedLabeledFields'),
                        'errorAutoHide'
                    );
                }
            } else {
                this.OffRoute = false;
                this.showMessage(
                    this.$tc('Msg.Error'),
                    this.$t('Msg.CheckRedLabeledFields'),
                    'errorAutoHide'
                );
            }
        },

        async insert() {
            if (!this.codeExist) {
                const param = this.prepareProtocol();
                await MedicationService.addProtocolSetup(param)
                    .then(response => {
                        if (response.status == '200') {
                            this.WasSaved = true;
                            this.pendingChanges = false;

                            if (!this.OffRoute) {
                                this.discardChanges();
                            }

                            setTimeout(
                                () =>
                                    this.$bvToast.toast(this.$t('Shared.SavedSuccessfully'), {
                                        title: this.$t('Msg.Success'),
                                        autoHideDelay: 5000,
                                        variant: 'success'
                                    }),
                                50
                            );
                        }
                    })
                    .catch(error => {
                        this.OffRoute = false;
                        this.showResponseErrorMessage(error);
                    });
            } else {
                this.showMessage(
                    this.$tc('Msg.Error'),
                    this.$t('DrugSetupProtocol.CodeExistMsg'),
                    'error'
                );
            }
        },

        async update() {
            const param = this.prepareProtocol();
            await MedicationService.updateProtocolSetup(this.$route.params.id, param)
                .then(response => {
                    if (response.status == '200') {
                        this.WasSaved = true;
                        this.pendingChanges = false;

                        if (!this.OffRoute) {
                            this.discardChanges();
                        }

                        setTimeout(
                            () =>
                                this.$bvToast.toast(this.$t('Shared.SavedSuccessfully'), {
                                    title: this.$t('Msg.Success'),
                                    autoHideDelay: 5000,
                                    variant: 'success'
                                }),
                            50
                        );
                    }
                })
                .catch(error => {
                    this.OffRoute = false;
                    this.showResponseErrorMessage(error);
                });
        },

        validate_required() {
            this.isCodeValid = !Utils.isNullOrEmptyString(this.editableItem.code);
            this.isDescriptionValid = !Utils.isNullOrEmptyString(this.editableItem.description);
            return this.isCodeValid && this.isDescriptionValid;
        },

        validate_prescriptions() {
            this.invalidStates.fill(false);
            let isPrescriptionsValid = this.editableItem.prescriptions.length !== 0;
            for (
                let index = 0;
                index < this.editableItem.prescriptions.length && isPrescriptionsValid;
                index++
            ) {
                let isValidateRequired = this.$refs[
                    `StandingOrderDrug_${index}`
                ][0].ValidateRequired();
                if (isValidateRequired) {
                    const item = this.editableItem.prescriptions[index];
                    this.duplicateTitration = this.existDuplicateTitration(
                        this.editableItem.prescriptions,
                        item.drugCode
                    );
                    isValidateRequired = !this.duplicateTitration;
                }

                isPrescriptionsValid &= isValidateRequired;
                if (!isValidateRequired) {
                    const indexOfVisible = this.visibleStates.indexOf(true);
                    if (indexOfVisible !== index) {
                        this.visibleStates[indexOfVisible] = false;
                        this.visibleStates[index] = true;
                    }

                    this.invalidStates[index] = true;
                }
            }

            return isPrescriptionsValid;
        },

        prepareProtocol() {
            const obj = {
                ...this.editableItem,
                standingOrderDrugs: []
            };

            for (let index = 0; index < this.editableItem.prescriptions.length; index++) {
                obj.standingOrderDrugs.push(
                    this.$refs[`StandingOrderDrug_${index}`][0].PrepareMedication()
                );
            }

            delete obj.prescriptions;
            return obj;
        },

        async onCodeConditionOk(result) {
            var type = result.FieldName;

            if (type.indexOf('Diagnosis') >= 0) {
                this.editableItem.diagCode = result.code;
                this.editableItem.diagDescription = result.description;
            } else if (type.indexOf('Drugs') >= 0) {
                this.$bvModal.hide(this.medicationsDlgId);
                if (result.code) {
                    let dCode = result.code.trim();
                    await DrugService.Get(dCode)
                        .then(response => {
                            if (response.status === 200 && response.data) {
                                const data = response.data;
                                let titrationOrder = this.getNextTitration(
                                    this.editableItem.prescriptions,
                                    data.drugCode
                                );

                                this.editableItem.prescriptions.push({
                                    drugCode: data.drugCode,
                                    drugName: data.drugname,
                                    activeIngredient: data.activeingred,
                                    dispensingCapacity: data.dispensingCapacity,
                                    form: data.form,
                                    reason: data.reason,
                                    routeName: data.route,
                                    unit: data.unitOfMeasure,
                                    whenSig: data.whenSig,
                                    sig: null,
                                    notes: null,
                                    action: data.action,
                                    day: data.defaultDays ?? 1,
                                    doseQty: 1,
                                    frequency: null,
                                    isReusable: data.isReusable,
                                    mode: null,
                                    qtytoTake: null,
                                    quantityOrdered: 0,
                                    titrationOrder: titrationOrder,
                                    marsTimes: [],
                                    recordId: null
                                });
                                this.pendingChanges = true;
                                this.visibleStates.push(false);
                                this.invalidStates.push(false);

                                this.editableItem.prescriptions.sort((a, b) => {
                                    const x = a.drugName.toLowerCase();
                                    const y = b.drugName.toLowerCase();
                                    return x < y ? -1 : x > y ? 1 : 0;
                                });

                                const index = this.editableItem.prescriptions.findIndex(
                                    x =>
                                        x.drugCode === data.drugCode &&
                                        x.titrationOrder == titrationOrder
                                );
                                if (~index) {
                                    this.visibleStates[index] = true;
                                }
                            }
                        })
                        .catch(error => {
                            this.showResponseErrorMessage(error);
                        });
                }
            }
        },

        getNextTitration(arr, code) {
            const drugRecords = arr.filter(item => item.drugCode === code);

            if (drugRecords.length === 0) return 1;

            const maxOrder = Math.max(...drugRecords.map(item => item.titrationOrder));
            return maxOrder + 1;
        },

        existDuplicateTitration(arr, code) {
            const drugRecords = arr.filter(item => item.drugCode === code);

            if (drugRecords.length === 0) return false;

            const uniqueTitrationOrder = [...new Set(drugRecords.map(item => item.titrationOrder))];
            return uniqueTitrationOrder.length !== drugRecords.length;
        },

        async addMedication() {
            await this.$bvModal.show(this.medicationsDlgId);
        },

        removeMedication(event, index) {
            event.preventDefault();
            this.$bvModal
                .msgBoxConfirm(this.$t('Msg.DeleteRecord'), {
                    title: this.$tc('Shared.DelRecord'),
                    okTitle: this.$t('Shared.TitleDelete'),
                    cancelTitle: this.$t('Shared.BtnCancel'),
                    footerClass: 'p-2',
                    titleClass: 'modal-title modal-type-warning',
                    id: 'modal_delete_drug'
                })
                .then(value => {
                    if (value) {
                        this.editableItem.prescriptions.splice(index, 1);
                        this.visibleStates.splice(index, 1);
                        this.invalidStates.splice(index, 1);
                        this.pendingChanges = true;
                    }
                });
        },

        getAction() {
            var param = {
                CodeType: 'ACTION',
                GroupCreation: 'SIG'
            };
            GlobalService.getCustomGlobalCodes(param)
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.ActionList = response.data;
                        this.ActionList.forEach(element => {
                            element.code = element.description;
                        });
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        getRouteName() {
            var param = {
                CodeType: 'ROUTE',
                GroupCreation: 'SIG'
            };
            GlobalService.getCustomGlobalCodes(param)
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.RouteNameList = response.data;
                        this.RouteNameList.forEach(element => {
                            element.code = element.description;
                        });
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        getWhen() {
            var param = {
                CodeType: 'WHEN',
                GroupCreation: 'SIG'
            };
            GlobalService.getCustomGlobalCodes(param)
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.WhenList = response.data;
                        this.WhenList.forEach(element => {
                            element.code = element.description;
                        });
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        getReason() {
            var param = {
                CodeType: 'REASON',
                GroupCreation: 'SIG'
            };
            GlobalService.getCustomGlobalCodes(param)
                .then(response => {
                    if (response.status == '200' && response.data) {
                        this.ReasonList = response.data;
                        this.ReasonList.forEach(element => {
                            element.code = element.description;
                        });
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        getPharmacyOptions() {
            PharmacyOptions.getPharmacyOptions()
                .then(response => {
                    if (response.status == '200' && response.data && response.data.length > 0) {
                        this.PharmacyOption = response.data[0];
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        getDrugProtocolSetup() {
            MedicationService.getStandingOrders({ code: this.$route.params.id, loadDetails: true })
                .then(response => {
                    if (response.status == '200' && response.data && response.data.length > 0) {
                        const item = response.data[0];
                        this.$set(this, 'editableItem', {
                            code: item.code,
                            description: item.description,
                            diagCode: item.diagCode,
                            diagDescription: item.diagDescription,
                            isActive: item.isActive,
                            isHealthProtocol: item.isHealthProtocol,
                            remarks: item.remarks,
                            prescriptions: item.standingOrderDrugs.map(data => ({
                                drugCode: data.drugCode,
                                drugName: data.drugName,
                                activeIngredient: data.activeIngredient,
                                dispensingCapacity: data.dispensingCapacity,
                                form: data.form,
                                reason: data.reason,
                                routeName: data.route,
                                unit: data.unit,
                                whenSig: data.whenSig,
                                sig: data.sig,
                                notes: data.notes,
                                action: data.action,
                                day: data.day,
                                doseQty: data.doseQty,
                                frequency: data.frequency,
                                frequencyDescription: data.frequencyDescription,
                                isReusable: data.isReusable,
                                mode: data.mode,
                                qtytoTake: data.qtytoTake,
                                quantityOrdered: data.quantityOrdered,
                                titrationOrder: data.titrationOrder,
                                marsTimes: data.marsTimes,
                                recordId: data.recordId
                            }))
                        });
                        this.editableItem.prescriptions.forEach(() => {
                            this.visibleStates.push(false);
                            this.invalidStates.push(false);
                        });
                    }
                })
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        async onLoad() {
            await Promise.allSettled([this.getPharmacyOptions()])
                .then(async result =>
                    result.length == 1
                        ? await Promise.allSettled([
                              this.getAction(),
                              this.getRouteName(),
                              this.getWhen(),
                              this.getReason()
                          ]).catch(error => {
                              this.showResponseErrorMessage(error);
                          })
                        : null
                )
                .catch(error => {
                    this.showResponseErrorMessage(error);
                });
        },

        async onCodeBlur(value) {
            if (!this.$attrs.editMode) {
                if (value.trim() !== '') {
                    var parameter = {
                        code: value
                    };

                    await MedicationService.getProtocolSetupByCode(parameter)
                        .then(response => {
                            if (response.status == '200' && response.data) {
                                if (response.data.length > 0) {
                                    this.showMessage(
                                        this.$tc('Msg.Error'),
                                        this.$t('DrugSetupProtocol.CodeExistMsg'),
                                        'error'
                                    );
                                    this.codeExist = true;
                                } else {
                                    this.codeExist = false;
                                }
                            }
                        })
                        .catch(error => {
                            this.showResponseErrorMessage(error);
                        });
                }
            }
        },

        async ClickOffRoute() {
            this.OffRoute = true;
            await this.saveOrEdit();
        },

        beforeWindowUnload(e) {
            if (this.pendingChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        }
    },

    async mounted() {
        await this.onLoad();

        if (this.$attrs.editMode) {
            this.getDrugProtocolSetup();
        }
    },

    created() {
        EventBus.$on('onChanged', e => {
            this.pendingChanges = this.pendingChanges || e.Changed;
        });
        window.addEventListener('beforeunload', this.beforeWindowUnload);
    },

    beforeDestroy() {
        EventBus.$off('onChanged');
        window.removeEventListener('beforeunload', this.beforeWindowUnload);
    },

    beforeRouteLeave(to, from, next) {
        if (!this.isReadOnly && this.pendingChanges) {
            this.$bvModal
                .msgBoxConfirm(this.$t('Msg.LeavingWithoutSaving'), {
                    title: this.$t('Shared.Confirm'),
                    okTitle: this.$t('Shared.BtnSave'),
                    cancelTitle: this.$t('Shared.BtnDismiss'),
                    footerClass: 'p-2',
                    titleClass: 'modal-title modal-type-warning'
                })
                .then(async value => {
                    if (!value) {
                        next();
                    } else {
                        await this.ClickOffRoute();
                        var f = this.WasSaved;
                        if (f) {
                            next();
                        }
                    }
                });
        } else {
            next();
        }
    }
};
</script>

<style scoped>
/* MODAL REMAKE */
.zebraAccordion {
    margin: 0 0;
}
.resetFontSize small {
    font-size: 0.95em;
}
.zebraAccordion .zebraRow .zebraItem {
    border-radius: 4px 4px 0 0;
    padding: 5px;
    width: 100%;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
}

.zebraAccordion .zebraRow:nth-child(odd) .zebraItem {
    background-color: #d4f7ff;
}
.zebraAccordion .zebraRow:nth-child(even) .zebraItem {
    background-color: #ecfcff;
}

.zebraAccordion .zebraRow .accordion .zebraItem.not-collapsed {
    background-color: #19bee1;
    color: #fff;
}
.zebraAccordion .zebraRow .accordion .zebraItem.text-error {
    background-color: #ff0d49;
    color: #fff;
}
.zebraAccordion .zebraRow .accordion .zebraItem:hover {
    background-color: #64d6f0;
    color: #fff;
}
.zebraAccordion .zebraRow .accordion .zebraItem.text-error:hover {
    background-color: #ff7092;
    color: #fff;
}
.accordionContent {
    border-top: 0;
    background: linear-gradient(173deg, #d9f4fb, #fff 30%);
    padding-top: 3em;
}
</style>
